import { normalizeNullableId } from '@/lib/services/occasions/occasions.utils'
import { getErrorMessage } from '@/lib/utils'
import { useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  SEASONAL_RECOMMENDATION_INTENT,
  SEASONAL_RECOMMENDATION_RESULT,
} from './seasonalRecommendation.constants'
import { buildSeasonalRecommendationTransition } from './seasonalRecommendationTransition'

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value ?? {}, key)

const isTimingOnly = (transitionContext) =>
  transitionContext.intent === SEASONAL_RECOMMENDATION_INTENT.USE_DEFAULT_TIMING

export function useSeasonalRecommendationWorkflow({
  form,
  defaultDesign,
  seasonalTags,
  selectedSeasonalTagId,
  recommendationMutation,
  onPendingChange,
}) {
  const requestIdRef = useRef(0)
  const [lastSnapshot, setLastSnapshot] = useState(null)
  const [health, setHealth] = useState(null)

  const applyRecommendation = (recommendation, seasonalTagId, transitionContext) => {
    const transition = buildSeasonalRecommendationTransition({
      values: form.store.state.values,
      recommendation,
      seasonalTagId,
      seasonalTags,
      defaultDesign,
      ...transitionContext,
    })
    if (transition.didChangeValues) {
      if (!isTimingOnly(transitionContext)) {
        setLastSnapshot(transition.snapshot)
      }
      for (const key of transition.changedFields) {
        form.setFieldValue(key, transition.values[key])
      }
    }
    if (!isTimingOnly(transitionContext)) {
      setHealth(transition.health)
    }

    return transition
  }

  const restoreTimingSelection = (transitionContext) => {
    const previousSchedule = transitionContext.previousSchedule ?? {}
    if (hasOwn(previousSchedule, 'timingMode')) {
      form.setFieldValue('schedule.timing_mode', previousSchedule.timingMode)
    }
    if (hasOwn(previousSchedule, 'profileId')) {
      form.setFieldValue('schedule.seasonal_schedule_profile_id', previousSchedule.profileId)
    }
  }

  const requestRecommendation = async (
    seasonalTagId = selectedSeasonalTagId,
    transitionContext = { intent: SEASONAL_RECOMMENDATION_INTENT.SHUFFLE }
  ) => {
    const normalizedSeasonalTagId = Number(seasonalTagId)
    if (!Number.isFinite(normalizedSeasonalTagId) || normalizedSeasonalTagId <= 0) {
      return SEASONAL_RECOMMENDATION_RESULT.FAILED
    }
    const requestId = ++requestIdRef.current
    onPendingChange?.(true)

    try {
      const currentDesignId = Number(form.getFieldValue('seasonal.seasonal_design_id'))
      const response = await recommendationMutation.mutateAsync({
        seasonalTagId: normalizedSeasonalTagId,
        excludeDesignIds: currentDesignId > 0 ? [currentDesignId] : [],
      })
      const recommendation =
        response && typeof response === 'object' && 'data' in response ? response.data : response
      if (requestId !== requestIdRef.current) return SEASONAL_RECOMMENDATION_RESULT.STALE
      const transition = applyRecommendation(recommendation, normalizedSeasonalTagId, {
        ...transitionContext,
        scheduleProfile: response?.schedule_profile ?? null,
      })
      if (
        isTimingOnly(transitionContext) &&
        transition.result !== SEASONAL_RECOMMENDATION_RESULT.APPLIED
      ) {
        restoreTimingSelection(transitionContext)
        toast.error('Default timing is not available for this occasion or season')
      }

      return transition.result
    } catch (error) {
      if (requestId !== requestIdRef.current) return SEASONAL_RECOMMENDATION_RESULT.STALE
      if (transitionContext.intent !== SEASONAL_RECOMMENDATION_INTENT.SHUFFLE) {
        if (isTimingOnly(transitionContext)) {
          restoreTimingSelection(transitionContext)
        } else {
          const previousSeasonalTag = transitionContext.previousSeasonalTag ?? {}
          if (hasOwn(previousSeasonalTag, 'formValue')) {
            form.setFieldValue('seasonal_tag_id', previousSeasonalTag.formValue)
          }
          form.setFieldValue(
            'seasonal.seasonal_tag_id',
            normalizeNullableId(
              hasOwn(previousSeasonalTag, 'seasonalId')
                ? previousSeasonalTag.seasonalId
                : previousSeasonalTag.formValue
            )
          )
        }
      }
      toast.error(
        isTimingOnly(transitionContext)
          ? 'Could not apply default timing'
          : 'Could not create another seasonal look',
        { description: getErrorMessage(error) }
      )

      return SEASONAL_RECOMMENDATION_RESULT.FAILED
    } finally {
      if (requestId === requestIdRef.current) {
        onPendingChange?.(false)
      }
    }
  }

  const undoRecommendation = () => {
    if (!lastSnapshot) return
    Object.entries(lastSnapshot).forEach(([key, value]) => form.setFieldValue(key, value))
    setLastSnapshot(null)
    setHealth(null)
  }

  const cancelRecommendation = () => {
    requestIdRef.current += 1
    onPendingChange?.(false)
  }

  return {
    cancelRecommendation,
    hasUndo: lastSnapshot !== null,
    health,
    requestRecommendation,
    undoRecommendation,
  }
}
